import { useEffect, useState } from 'react';

/** The id of the build running in this tab (see vite.config.ts). */
export const APP_VERSION: string = import.meta.env.VITE_APP_VERSION ?? 'dev';

/** How often an open tab checks for a new deployment (also checked when the tab regains focus). */
export const VERSION_CHECK_MS = 5 * 60_000;

async function deployedVersion(): Promise<string | null> {
  try {
    const res = await fetch('/version.json', { cache: 'no-store' });
    if (!res.ok) return null;
    const body: unknown = await res.json();
    const version = (body as { version?: unknown } | null)?.version;
    return typeof version === 'string' && version ? version : null;
  } catch {
    // Offline, or no version file (the dev server answers with index.html).
    return null;
  }
}

/** True once a build other than the running one has been deployed. */
export function useNewVersionAvailable(): boolean {
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    if (available) return;
    let active = true;
    const check = async () => {
      const deployed = await deployedVersion();
      if (active && deployed && deployed !== APP_VERSION) setAvailable(true);
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };
    const timer = setInterval(check, VERSION_CHECK_MS);
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      active = false;
      clearInterval(timer);
      window.removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [available]);

  return available;
}
